import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getResidents } from '@/api/residents';
import { useAuth } from '@/contexts/AuthContext';
import type { Resident } from '@/types/resources/resident';

interface ResidentSelectorProps {
  disabled?: boolean;
  excludeAssigned?: boolean;
  fieldName?: string;
  onResidentDataChange?: (resident: { id: string; name: string } | null) => void;
}

export const ResidentSelector: React.FC<ResidentSelectorProps> = ({
  disabled = false,
  excludeAssigned = false,
  fieldName = 'residentId',
  onResidentDataChange
}) => {
  const { t } = useTranslation(['residents', 'common']);
  const { firestoreUser } = useAuth();
  const form = Form.useFormInstance();

  // Fetch residents
  const { data: residentsData, isLoading } = useQuery({
    queryKey: ['residents', 'all', firestoreUser?.workspace?.id, excludeAssigned],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getResidents({ firestoreUser });
    },
    enabled: !!firestoreUser?.workspace?.id
  });

  const availableResidents = excludeAssigned 
    ? residentsData?.residents?.filter((resident: Resident) => !resident.apartmentUnit) || []
    : residentsData?.residents || [];

  // Initialize resident data from form values when component mounts or form values change
  useEffect(() => {
    const residentId = form.getFieldValue(fieldName);
    if (residentId && residentsData?.residents) {
      const resident = residentsData.residents.find((r: Resident) => r.id === residentId);
      onResidentDataChange?.(resident ? { 
        id: resident.id, 
        name: resident.user?.displayName || resident.id 
      } : null);
    }
  }, [form, residentsData, onResidentDataChange, fieldName]);

  const handleResidentChange = (residentId: string) => {
    // Provide resident data to parent
    if (residentId) {
      const resident = residentsData?.residents?.find((r: Resident) => r.id === residentId);
      onResidentDataChange?.(resident ? { 
        id: resident.id, 
        name: resident.user?.displayName || resident.id 
      } : null);
    } else {
      onResidentDataChange?.(null);
    }
  };

  return (
    <Form.Item
      name={fieldName}
      label={t('residents:fields.name')}
    >
      <Select
        placeholder={isLoading ? t('common:loading') : t('residents:fields.selectResident')}
        onChange={handleResidentChange}
        disabled={disabled || isLoading}
        allowClear
        loading={isLoading}
        options={availableResidents.map((resident: Resident) => ({
          value: resident.id,
          label: resident.user?.displayName || resident.id,
          disabled: excludeAssigned ? !!resident.apartmentUnit : false
        }))}
      />
    </Form.Item>
  );
}; 